import { TableUI } from "components/design/TableUI";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  ConditionalRender,
  TaskTrackerTrackerFilterType,
} from "./SalesTaxTracker";
import { useMemo } from "react";
import { SelectFilter } from "./SelectFilter";
import { Status } from "./Status";
import {
  PAYMENT_SCHEDULE,
  TASK_TYPE,
  TransactionType,
} from "store/apis/taskTrackerSalesTax";
import dayjs from "dayjs";
import { DISPLAY_DATE, YYYY } from "constants/date";
import {
  PreparerColumn,
  ServiceTeamColumn,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { MONTHLY } from "constants/salesTaxRegistrationStatusType";
import { isOverdue } from "../Filings/Filing";
import { FILED } from "constants/filing";
import { useQuery } from "hooks/useQuery";
import { SALES_TAX } from "constants/apps";
import Table from "components/DesignSystem/Table/V2/Table";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { openLink } from "utils/openLink";
import { CustomChatSlider } from "components/CustomChatSlider/CustomChatSlider";
import { useUnreadFromChannels } from "hooks/useUnreadFromChannels";

const columnHelper = createColumnHelper<TransactionType>();

export const redirectToFiling = ({
  startDate,
  groupId,
  entityId,
  filingId,
}: {
  startDate?: string;
  groupId: string;
  entityId: string;
  filingId?: string;
}) => {
  if (!filingId) {
    let taskLink = `/sales-tax/state-filings?company=${groupId}&entity=${entityId}`;
    openLink(taskLink);
    return;
  }

  let taskLink = `/sales-tax/state-filings?company=${groupId}&entity=${entityId}&tax_year=${dayjs(
    startDate
  ).format(YYYY)}&filing_id=${filingId}`;

  openLink(taskLink);
};

export const redirectToRegistration = ({
  groupId,
  entityId,
  stateId,
}: {
  groupId: string;
  entityId: string;
  stateId?: string;
}) => {
  if (!stateId) {
    const taskLink = `/sales-tax/registrations?company=${groupId}&entity=${entityId}`;
    openLink(taskLink);
    return;
  }
  const taskLink = `/sales-tax/registrations?company=${groupId}&entity=${entityId}&state_id=${stateId}&type=existing`;
  openLink(taskLink);
};

export const statusMap = {
  IN_CART: "In cart",
  PROCESSING: "Processing",
  FILED: "Filed",
  NOT_STARTED: "Not started",
  REGISTERED: "Registered",
  VALIDATING: "In cart",
  IN_REVIEW: "In review",
  PENDING_DETAILS: "Not started",
};

const paymentScheduleMap = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  SEMI_ANNUALLY: "Semi annually",
  ANNUALLY: "Annually",
};

export const SalesTaxTrackerTable = ({
  filter,
  onRowClick,
  data,
  isEmpty,
  perPage,
}: {
  filter: TaskTrackerTrackerFilterType;
  onRowClick: (a: any, openChat: boolean) => void;
  data: TransactionType[];
  isEmpty?: boolean;
  perPage: number;
}) => {
  const query = useQuery();

  const selectedTab = query.get("tab") || TASK_TYPE.FILINGS;

  const tableData = useMemo(() => data || [], [data]);

  const { values } = filter;

  const { isForeignCA } = useRoleBasedView();

  const registrationColumns = useMemo(
    () => [
      columnHelper.accessor("entity", {
        header: "Entity and Group",
        size: 15,
        cell: (info) => {
          const rowData = info.row.original;
          const groupId = rowData?.group?.uuid;
          const groupDetailsRoute = isForeignCA
            ? `/practice/crm/${groupId}`
            : `/crm/${groupId}`;

          const openCompany = (
            e: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => {
            e.stopPropagation();
            openLink(groupDetailsRoute, groupId);
          };
          return (
            <div>
              <div
                onClick={openCompany}
                className="t-cursor-pointer t-max-w-fit"
              >
                <p className="t-m-0 t-text-purple t-text-body-sm hover:t-underline">
                  {rowData?.entity?.name}
                </p>
              </div>
              <p className="t-m-0 t-text-text-30 t-text-body-sm">
                {rowData?.group?.name}
              </p>
            </div>
          );
        },
      }),
      columnHelper.accessor("task_type", {
        header: "Task Type",
        size: 15,
        cell: (info) => {
          const rowData = info.row.original;
          const openTask = (
            e: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => {
            e.stopPropagation();
            redirectToRegistration({
              groupId: rowData?.group?.uuid,
              entityId: rowData?.entity?.uuid,
              stateId: rowData?.state_id,
            });
          };
          return (
            <div className="t-cursor-pointer t-max-w-fit" onClick={openTask}>
              <p className="t-m-0 t-text-purple t-text-body-sm hover:t-underline">
                {info.getValue()}
              </p>
            </div>
          );
        },
      }),
      columnHelper.accessor("state", {
        header: () => <SelectFilter filter={filter} name="State" />,
        size: 10,
      }),
      columnHelper.accessor("status", {
        header: () => <SelectFilter filter={filter} name="Task Status" />,
        size: 10,
        cell: (info: any) => (
          <Status status={info.getValue()}>
            {statusMap[info.getValue() as keyof typeof statusMap]}
          </Status>
        ),
      }),
      columnHelper.accessor("request_date", {
        header: "Request date",
        size: 10,
        cell: (info) => {
          return (
            <ConditionalRender
              condition={Boolean(info.getValue())}
              fallback="-"
            >
              {dayjs(info.getValue()).format(DISPLAY_DATE)}
            </ConditionalRender>
          );
        },
      }),
      columnHelper.accessor("service_team", {
        header: () => <SelectFilter filter={filter} name="Service Team" />,
        size: 12,
        cell: (info) => {
          const taskData = {
            uuid: info.row.original.uuid,
            assigned_team: info.row.original.service_team,
          };
          return <ServiceTeamColumn tracker={SALES_TAX} taskData={taskData} />;
        },
      }),
      columnHelper.accessor("preparer", {
        header: "Preparer",
        size: 13,
        cell: (info) => {
          const taskData = {
            uuid: info.row.original.uuid,
            assigned_team: info.row.original.service_team,
            assigned_to: {
              uuid: info.row.original.preparer?.profile_id,
              name: info.row.original.preparer?.name,
            },
          };
          return <PreparerColumn tracker={SALES_TAX} taskData={taskData} />;
        },
      }),
      columnHelper.accessor("preparer.assigned_on", {
        header: "Assigned on",
        size: 10,
        cell: (info) => {
          return (
            <ConditionalRender
              fallback="-"
              condition={Boolean(info.getValue())}
            >
              {dayjs(info.getValue()).format(DISPLAY_DATE)}
            </ConditionalRender>
          );
        },
      }),
      columnHelper.accessor("channel_url", {
        header: "",
        cell: (info) => {
          const defaultChannelId = info.getValue();
          if (!defaultChannelId) {
            return null;
          }
          return (
            <CustomChatSlider
              openTaskChat={() => onRowClick(info.row, true)}
              defaultChannelId={defaultChannelId}
              defaultUnreadCount={channelUnreadMap[defaultChannelId]}
              taskId={defaultChannelId}
            />
          );
        },
        size: 5,
      }),
    ],
    []
  );
  //@ts-ignore
  const channelUrls: string[] = data
    .filter((t) => t.channel_url !== null)
    .map((t) => t.channel_url);

  const channelUnreadMap = useUnreadFromChannels({
    channelUrls,
    queryOptions: { limit: perPage },
  });

  const filingColumns = useMemo(
    () => [
      columnHelper.accessor("entity", {
        header: "Entity and Group",
        size: 15,
        cell: (info) => {
          const rowData = info.row.original;

          const groupId = rowData?.group?.uuid;
          const groupDetailsRoute = isForeignCA
            ? `/practice/crm/${groupId}`
            : `/crm/${groupId}`;

          const openCompany = (
            e: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => {
            e.stopPropagation();
            openLink(groupDetailsRoute, groupId);
          };
          return (
            <div>
              <div
                className="t-cursor-pointer t-max-w-fit"
                onClick={openCompany}
              >
                <p className="t-m-0 t-text-purple t-text-body-sm hover:t-underline">
                  {rowData?.entity?.name}
                </p>
              </div>
              <p className="t-m-0 t-text-text-30 t-text-body-sm">
                {rowData?.group?.name}
              </p>
            </div>
          );
        },
      }),
      columnHelper.accessor("task_type", {
        header: () => <SelectFilter filter={filter} name="Task Type" />,
        size: 15,
        cell: (info) => {
          const rowData = info.row.original;

          const openTask = (
            e: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => {
            e.stopPropagation();
            redirectToFiling({
              startDate: rowData?.start_date,
              groupId: rowData?.group?.uuid,
              entityId: rowData?.entity?.uuid,
              filingId: rowData?.uuid,
            });
          };
          return (
            <div>
              <div className="t-cursor-pointer t-max-w-fit" onClick={openTask}>
                <p className="t-m-0 t-text-purple t-text-body-sm hover:t-underline">
                  {info.getValue()}
                </p>
              </div>
              <p className="t-m-0 t-text-text-30 t-text-body-sm">
                {rowData?.payment_schedule !== PAYMENT_SCHEDULE.MONTHLY && (
                  <span>{dayjs(rowData?.start_date).format("MMM")} - </span>
                )}
                {dayjs(rowData?.end_date).format("MMM YYYY")}{" "}
                <span className="t-text-neutral-20">|</span>{" "}
                {rowData?.payment_schedule &&
                  paymentScheduleMap[
                    rowData?.payment_schedule as keyof typeof paymentScheduleMap
                  ]}
              </p>
            </div>
          );
        },
      }),
      columnHelper.accessor("state", {
        header: () => <SelectFilter filter={filter} name="State" />,
        size: 10,
      }),
      columnHelper.accessor("status", {
        header: () => <SelectFilter filter={filter} name="Task Status" />,
        size: 10,
        cell: (info: any) => (
          <Status status={info.getValue()}>
            {statusMap[info.getValue() as keyof typeof statusMap]}
          </Status>
        ),
      }),
      columnHelper.accessor("deadline", {
        header: "Deadline",
        size: 10,
        cell: (info: any) => {
          const rowData = info.row.original;
          return (
            <div>
              <p className="t-m-0">
                <ConditionalRender
                  condition={Boolean(info.getValue())}
                  fallback="-"
                >
                  {dayjs(info.getValue()).format(DISPLAY_DATE)}
                </ConditionalRender>
              </p>
              <ConditionalRender
                fallback={null}
                condition={Boolean(
                  isOverdue(info.getValue()) && rowData.status !== FILED
                )}
              >
                <p className="t-m-0 t-text-red t-text-body-sm">Overdue</p>
              </ConditionalRender>
            </div>
          );
        },
      }),
      columnHelper.accessor("service_team", {
        header: () => <SelectFilter filter={filter} name="Service Team" />,
        size: 13,
        cell: (info) => {
          const taskData = {
            uuid: info.row.original.uuid,
            assigned_team: info.row.original.service_team,
          };
          return <ServiceTeamColumn tracker={SALES_TAX} taskData={taskData} />;
        },
      }),
      columnHelper.accessor("preparer", {
        header: "Preparer",
        size: 12,
        cell: (info) => {
          const taskData = {
            uuid: info.row.original.uuid,
            assigned_team: info.row.original.service_team,
            assigned_to: {
              uuid: info.row.original.preparer?.profile_id,
              name: info.row.original.preparer?.name,
            },
          };
          return <PreparerColumn tracker={SALES_TAX} taskData={taskData} />;
        },
      }),
      columnHelper.accessor("preparer.assigned_on", {
        header: "Assigned on",
        size: 10,
        cell: (info) => {
          return (
            <ConditionalRender
              fallback="-"
              condition={Boolean(info.getValue())}
            >
              {dayjs(info.getValue()).format(DISPLAY_DATE)}
            </ConditionalRender>
          );
        },
      }),
      columnHelper.accessor("channel_url", {
        header: "",
        cell: (info) => {
          const defaultChannelId = info.getValue();
          if (!defaultChannelId) {
            return null;
          }
          return (
            <CustomChatSlider
              openTaskChat={() => onRowClick(info.row, true)}
              defaultChannelId={defaultChannelId}
              defaultUnreadCount={channelUnreadMap[defaultChannelId]}
              taskId={defaultChannelId}
            />
          );
        },
        size: 5,
      }),
    ],
    [filter, values]
  );

  const columns = useMemo(() => {
    const rightColumns =
      selectedTab === TASK_TYPE.REGISTRATIONS
        ? registrationColumns
        : filingColumns;
    return rightColumns || [];
  }, [selectedTab, registrationColumns, filingColumns]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
    getRowId: (row) => row.uuid,
    enableSorting: false,
  });
  return (
    <div className="t-h-full t-overflow-x-auto" style={{ maxWidth: "260rem" }}>
      <div style={{ width: "85rem" }} className="t-h-full">
        <TableUI table={table} enableSort size="large" sticky height="100%">
          {({ row }) => (
            <Table.Row
              onRowClick={() => onRowClick(row, false)}
              key={row.original.uuid}
              className="hover:t-bg-surface-lighter-grey"
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <Table.Cell
                    key={cell.id}
                    style={{
                      width: `${cell.column.getSize()}%`,
                      padding: "10px 12px",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          )}
        </TableUI>
      </div>
      {isEmpty && (
        <div className="t-flex t-items-center t-justify-center t-h-[300px]">
          <EmptyScreen text="No task found" className="!t-h-full" />
        </div>
      )}
    </div>
  );
};
